import { Script, ScriptBreakdown } from './api';

// Use environment variable if set, otherwise use relative path
const API_URL = process.env.REACT_APP_API_URL || '';

export interface WorkflowPhase {
  phase: number;
  status: 'pending' | 'running' | 'completed' | 'error';
  result?: any;
  error?: string;
}

export interface WorkflowCallbacks {
  onPhaseStart?: (phase: number) => void;
  onPhaseComplete?: (phase: number, result: any) => void;
  onPhaseError?: (phase: number, error: string) => void;
  onChunk?: (phase: number, chunk: string) => void;
  onScriptGenerated?: (script: Script, breakdown?: ScriptBreakdown) => void;
  onContextCreated?: (contextId: string) => void;
  onWorkflowComplete?: () => void;
  onMetadataReview?: (reviewData: any) => Promise<boolean>; // Returns true to continue, false to cancel
  onTaskConfirmation?: (taskData: any) => Promise<{ confirmed: boolean; selectedTask?: string }>; // Task confirmation callback
  onMetadata?: (category: string, data: any) => void; // Metadata event callback
  onPhaseChange?: (phase: number) => void;
  onDataHealthReview?: (healthData: any) => Promise<Record<string, string> | null>; // Returns user choices or null to skip
  onSaveDataHandling?: (dataHandlingChoices: Record<string, any>) => Promise<boolean>; // Returns true if saved
  onFileRelationshipReview?: (relationshipData: any) => Promise<any | null>; // Returns confirmed relationships or null to skip
}

export class WorkflowOrchestrator {
  private phases: WorkflowPhase[] = [
    { phase: 1, status: 'pending' },
    { phase: 2, status: 'pending' },
    { phase: 3, status: 'pending' },
    { phase: 4, status: 'pending' }
  ];
  
  private contextId: string | null = null;
  private scriptId: string | null = null;
  private fileIds: string[] | undefined;
  private revisionCount: number = 0;
  private maxRevisions: number = 2;
  private maxRetries: number = 3;
  private retryDelay: number = 1000; // Start with 1 second
  private dataHandlingChoices: Record<string, any> | null = null; // Store user's data handling choices
  private fileRelationships: any | null = null; // Store confirmed file relationships
  
  constructor(private callbacks: WorkflowCallbacks) {}
  
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }
  
  async executeWorkflow(
    sessionId: string,
    message: string,
    fileIds?: string[]
  ): Promise<void> {
    console.log('[WorkflowOrchestrator] executeWorkflow called with:', {
      sessionId,
      messageLength: message.length,
      fileIds,
      fileCount: fileIds?.length || 0
    });
    
    try {
      // Store fileIds for later use
      this.fileIds = fileIds;
      
      // Phase 1: Requirements Analysis
      await this.executePhase1(sessionId, message, fileIds);
      
      // Check if Phase 1 succeeded
      if (this.phases[0].status === 'error') {
        throw new Error('Phase 1 failed, cannot continue workflow');
      }
      
      // Check if Phase 1 returned metadata for review
      const phase1Result = this.phases[0].result;
      if (phase1Result?.metadata_review && this.callbacks.onMetadataReview) {
        console.log('[Workflow] Metadata review required, pausing workflow');
        
        // Add file_id to metadata review data (use first file if multiple)
        // Also preserve total_columns and selected_columns from Phase 1
        const metadataReviewWithFileId = {
          ...phase1Result.metadata_review,
          file_id: this.fileIds?.[0] || phase1Result.metadata_review.file_id,
          total_columns: phase1Result.metadata_review.total_columns,
          selected_columns: phase1Result.metadata_review.selected_columns
        };
        
        // Call the metadata review callback and wait for user decision
        const shouldContinue = await this.callbacks.onMetadataReview(metadataReviewWithFileId);
        
        if (!shouldContinue) {
          console.log('[Workflow] User cancelled metadata review, stopping workflow');
          return;
        }
        
        console.log('[Workflow] User confirmed metadata');
        
        // NOTE: Data health review is now handled AFTER metadata confirmation in onMetadataReview callback
        // This ensures users confirm columns first, then handle data quality issues on confirmed columns only
        
        // Check if file relationship review is needed (for multi-file workflows)
        console.log('[Workflow] Phase 1 Result:', phase1Result);
        console.log('[Workflow] File relationships data:', phase1Result.file_relationships);
        console.log('[Workflow] Has callback:', !!this.callbacks.onFileRelationshipReview);
        
        if (phase1Result.file_relationships && phase1Result.file_relationships.requires_review && this.callbacks.onFileRelationshipReview) {
          console.log('[Workflow] File relationships detected, showing relationship review');
          
          const confirmedRelationships = await this.callbacks.onFileRelationshipReview(phase1Result.file_relationships);
          
          if (confirmedRelationships) {
            console.log('[Workflow] User confirmed file relationships');
            this.fileRelationships = confirmedRelationships;
            
            // Send confirmed relationships to backend to update context
            await fetch(`${API_URL ? API_URL : ''}/api/workflow/context/${this.contextId}/relationships`, {
              method: 'POST',
              headers: this.getAuthHeaders(),
              body: JSON.stringify({
                file_relationships: confirmedRelationships
              })
            });
          } else {
            console.log('[Workflow] User skipped file relationship review');
          }
        }
        
        console.log('[Workflow] Continuing to Phase 2');
      }
      
      // Phase 2: Script Generation
      await this.executePhase2(sessionId);
      
      // Check if Phase 2 succeeded
      if (this.phases[1].status === 'error') {
        throw new Error('Phase 2 failed, cannot continue workflow');
      }
      
      // Phase 3: QA Review
      await this.executePhase3(sessionId);
      
      // In the new flow, Phase 3 completes and user can continue chatting
      // No automatic Phase 4 - user decides what to do next via conversation
      
      this.callbacks.onWorkflowComplete?.();
      
    } catch (error) {
      console.error('Workflow execution error:', error);
      
      // Provide more specific error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      const failedPhase = this.phases.find(p => p.status === 'error');
      
      if (failedPhase) {
        throw new Error(`Workflow failed at Phase ${failedPhase.phase}: ${errorMessage}`);
      } else {
        throw error;
      }
    }
  }
  
  async resumeFromContext(
    sessionId: string, 
    contextId: string, 
    hasScript: boolean = false,
    scriptId?: string
  ): Promise<void> {
    try {
      console.log('[Workflow] Resuming from context:', contextId, 'hasScript:', hasScript, 'scriptId:', scriptId);
      
      // Set the context ID to resume from
      this.contextId = contextId;
      
      // Set the script ID if provided (when resuming with existing script)
      if (scriptId) {
        this.scriptId = scriptId;
      }
      
      // Mark Phase 1 as completed since we're resuming
      this.updatePhaseStatus(1, 'completed');
      
      if (!hasScript) {
        // Phase 2: Script Generation (if not already done)
        await this.executePhase2(sessionId);
        
        // Check if Phase 2 succeeded
        if (this.phases[1].status === 'error') {
          throw new Error('Phase 2 failed, cannot continue workflow');
        }
      } else {
        // Mark Phase 2 as completed since script already exists
        this.updatePhaseStatus(2, 'completed');
        console.log('[Workflow] Skipping Phase 2 - script already generated');
      }
      
      // Phase 3: QA Review (always run this when resuming)
      await this.executePhase3(sessionId);
      
      // In the new flow, Phase 3 completes and user can continue chatting
      // No automatic Phase 4 - user decides what to do next via conversation
      
      this.callbacks.onWorkflowComplete?.();
      
    } catch (error) {
      console.error('Workflow resume error:', error);
      
      // Provide more specific error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      const failedPhase = this.phases.find(p => p.status === 'error');
      
      if (failedPhase) {
        throw new Error(`Workflow resume failed at Phase ${failedPhase.phase}: ${errorMessage}`);
      } else {
        throw error;
      }
    }
  }
  
  private async executePhase1(
    sessionId: string,
    message: string,
    fileIds?: string[]
  ): Promise<void> {
    this.updatePhaseStatus(1, 'running');
    this.callbacks.onPhaseStart?.(1);
    this.callbacks.onPhaseChange?.(1);
    
    try {
      const result = await this.executeWithRetry(async () => {
        const response = await fetch(`${API_URL ? API_URL : ''}/api/workflow/phase1/analyze`, {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            session_id: sessionId,
            message,
            file_ids: fileIds
          })
        });
        
        if (!response.ok) {
          throw new Error(`Phase 1 failed: ${response.statusText}`);
        }
        
        return this.processStreamingResponse(response, 1);
      });
      
      if (result.context_id) {
        this.contextId = result.context_id;
        this.callbacks.onContextCreated?.(result.context_id);
      }
      
      // Check if task confirmation is needed
      if (result.taskConfirmation && this.callbacks.onTaskConfirmation) {
        console.log('[Workflow] Task confirmation required');
        const userDecision = await this.callbacks.onTaskConfirmation(result.taskConfirmation);
        
        // Send confirmation to backend
        const confirmResponse = await fetch(`${API_URL ? API_URL : ''}/api/workflow/task-confirmation`, {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            context_id: this.contextId,
            confirmed: userDecision.confirmed,
            selected_task: userDecision.selectedTask
          })
        });
        
        if (!confirmResponse.ok) {
          console.error('Failed to send task confirmation');
        }
      }
      
      // Store the complete result including metadata_review
      this.phases[0].result = result;
      
      this.updatePhaseStatus(1, 'completed');
      this.callbacks.onPhaseComplete?.(1, result);
      
    } catch (error) {
      this.updatePhaseStatus(1, 'error');
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.callbacks.onPhaseError?.(1, errorMessage);
      throw error;
    }
  }
  
  private async executePhase2(sessionId: string): Promise<void> {
    if (!this.contextId) {
      throw new Error('No context ID available for Phase 2');
    }
    
    this.updatePhaseStatus(2, 'running');
    this.callbacks.onPhaseStart?.(2);
    this.callbacks.onPhaseChange?.(2);
    
    try {
      const response = await fetch(`${API_URL ? API_URL : ''}/api/workflow/phase2/generate`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          session_id: sessionId,
          context_id: this.contextId
        })
      });
      
      if (!response.ok) {
        throw new Error(`Phase 2 failed: ${response.statusText}`);
      }
      
      const result = await this.processStreamingResponse(response, 2);
      
      // Check if we got a valid result
      if (!result || Object.keys(result).length === 0) {
        console.warn('Phase 2 completed with empty result - script may not have been saved');
      }
      
      if (result.script_id) {
        this.scriptId = result.script_id;
      }
      
      if (result.script) {
        // Convert to Script object
        const script: Script = {
          script_id: result.script_id || '',
          session_id: sessionId,
          code: result.script.content || '',
          language: result.script.language || 'python',
          created_at: new Date().toISOString(),
          executed: false,
          breakdown: result.breakdown
        };
        
        this.callbacks.onScriptGenerated?.(script, result.breakdown);
      } else if (!result.script && !result.script_id && result.streamedContent) {
        // Script extraction failed but we have streamed content
        console.warn('Script extraction failed, creating fallback script from streamed content');
        
        // Try to extract Python code from streamed content
        let code = result.streamedContent;
        const pythonMatch = result.streamedContent.match(/```python[\s\n]([\s\S]*?)```/);
        if (pythonMatch) {
          code = pythonMatch[1].trim();
        }
        
        // Create a temporary script object
        const fallbackScript: Script = {
          script_id: 'streaming-script',
          session_id: sessionId,
          code: code,
          language: 'python',
          created_at: new Date().toISOString(),
          executed: false,
          breakdown: result.breakdown
        };
        
        this.callbacks.onScriptGenerated?.(fallbackScript, result.breakdown);
      } else if (!result.script && !result.script_id) {
        // No script was extracted - this is a problem
        console.error('Phase 2 completed but no script was generated');
      }
      
      this.updatePhaseStatus(2, 'completed');
      this.callbacks.onPhaseComplete?.(2, result);
      
      // Check if we should prompt to save data handling choices
      if (result.should_save_data_handling && this.dataHandlingChoices && this.callbacks.onSaveDataHandling) {
        console.log('[Workflow] Prompting to save data handling choices');
        const saved = await this.callbacks.onSaveDataHandling(this.dataHandlingChoices);
        if (saved) {
          console.log('[Workflow] Data handling choices saved successfully');
        }
      }
      
    } catch (error) {
      this.updatePhaseStatus(2, 'error');
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.callbacks.onPhaseError?.(2, errorMessage);
      throw error;
    }
  }
  
  private async executePhase3(sessionId: string): Promise<void> {
    if (!this.contextId || !this.scriptId) {
      throw new Error('Missing context or script ID for Phase 3');
    }
    
    this.updatePhaseStatus(3, 'running');
    this.callbacks.onPhaseStart?.(3);
    this.callbacks.onPhaseChange?.(3);
    
    try {
      const response = await fetch(`${API_URL ? API_URL : ''}/api/workflow/phase3/qa`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          session_id: sessionId,
          context_id: this.contextId,
          script_id: this.scriptId
        })
      });
      
      if (!response.ok) {
        throw new Error(`Phase 3 failed: ${response.statusText}`);
      }
      
      const result = await this.processStreamingResponse(response, 3);
      
      // Store the result for later checking
      this.phases[2].result = result;
      
      this.updatePhaseStatus(3, 'completed');
      this.callbacks.onPhaseComplete?.(3, result);
      
    } catch (error) {
      this.updatePhaseStatus(3, 'error');
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.callbacks.onPhaseError?.(3, errorMessage);
      throw error;
    }
  }
  
  public async triggerRevision(sessionId: string, userRequest: string): Promise<void> {
    if (!this.contextId || !this.scriptId) {
      throw new Error('Cannot revise: missing context or script ID');
    }
    
    // Execute Phase 4 with the user's revision request
    await this.executePhase4(sessionId, userRequest);
  }
  
  private async executePhase4(sessionId: string, userRequest?: string): Promise<void> {
    if (!this.contextId || !this.scriptId) {
      throw new Error('Missing context or script ID for Phase 4');
    }
    
    // Store the original script ID before revision
    const originalScriptId = this.scriptId;
    
    this.updatePhaseStatus(4, 'running');
    this.callbacks.onPhaseStart?.(4);
    this.callbacks.onPhaseChange?.(4);
    this.revisionCount++;
    
    try {
      const response = await fetch(`${API_URL ? API_URL : ''}/api/workflow/phase4/revise`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          session_id: sessionId,
          context_id: this.contextId,
          script_id: this.scriptId,
          user_request: userRequest || ''
        })
      });
      
      if (!response.ok) {
        throw new Error(`Phase 4 failed: ${response.statusText}`);
      }
      
      const result = await this.processStreamingResponse(response, 4);
      
      // Update scriptId if a new script was created
      if (result.script_id) {
        this.scriptId = result.script_id;
        console.log(`Phase 4 created new script: ${this.scriptId}`);
      }
      
      if (result.script) {
        // Update script with revision - use new script_id if provided
        const script: Script = {
          script_id: result.script_id || this.scriptId,
          session_id: sessionId,
          code: result.script.content || '',
          language: result.script.language || 'python',
          created_at: new Date().toISOString(),
          executed: false,
          breakdown: result.breakdown,
          version: 2,  // This is a revision
          parent_script_id: originalScriptId  // Reference to the original script before revision
        };
        
        this.callbacks.onScriptGenerated?.(script, result.breakdown);
      } else if (!result.script && result.streamedContent) {
        // Script extraction failed but we have streamed content (like Phase 2)
        console.warn('Phase 4: Script extraction failed, creating fallback script from streamed content');
        
        // Try to extract Python code from streamed content
        let code = result.streamedContent;
        const pythonMatch = result.streamedContent.match(/```python[\s\n]([\s\S]*?)```/);
        if (pythonMatch) {
          code = pythonMatch[1].trim();
        }
        
        // Create a script object with the revised code
        const fallbackScript: Script = {
          script_id: result.script_id || this.scriptId,
          session_id: sessionId,
          code: code,
          language: 'python',
          created_at: new Date().toISOString(),
          executed: false,
          breakdown: result.breakdown,
          version: 2,
          parent_script_id: originalScriptId
        };
        
        this.callbacks.onScriptGenerated?.(fallbackScript, result.breakdown);
      } else if (!result.script && !result.streamedContent) {
        // No script was extracted and no streamed content
        console.error('Phase 4 completed but no script was generated');
      }
      
      this.updatePhaseStatus(4, 'completed');
      this.callbacks.onPhaseComplete?.(4, result);
      
    } catch (error) {
      this.updatePhaseStatus(4, 'error');
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.callbacks.onPhaseError?.(4, errorMessage);
      throw error;
    }
  }
  
  private async processStreamingResponse(
    response: Response,
    phase: number
  ): Promise<any> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }
    
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult: any = {};
    let streamedContent = '';
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      
      // Process complete lines
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      
      for (const line of lines) {
        if (!line.trim()) continue;
        
        try {
          const data = JSON.parse(line);
          
          // Handle metadata events
          if (data.type === 'metadata' && data.category && data.data) {
            this.callbacks.onMetadata?.(data.category, data.data);
          }
          // Handle task confirmation request
          else if (data.type === 'task_confirmation' && data.data) {
            if (this.callbacks.onTaskConfirmation) {
              // Store task confirmation data for handling after streaming
              finalResult = { ...finalResult, taskConfirmation: data.data };
            }
          } else if (data.type === 'chunk' && data.content) {
            this.callbacks.onChunk?.(phase, data.content);
            // Track streamed content for Phase 2 and Phase 4 (script generation/revision)
            if (phase === 2 || phase === 4) {
              streamedContent += data.content;
            }
          } else if (data.type === 'complete') {
            finalResult = data;
            // For Phase 2 and Phase 4, preserve streamed content if no script was extracted
            if ((phase === 2 || phase === 4) && streamedContent && !finalResult.script) {
              finalResult.streamedContent = streamedContent;
            }
          } else if (data.type === 'error') {
            throw new Error(data.message);
          }
        } catch (e) {
          console.error('Error parsing streaming data:', e);
          console.error('Raw line that failed to parse:', line);
          // If it's a string that looks like an error message, throw it
          if (line.includes('Error:') || line.includes('error:')) {
            throw new Error(`Backend error: ${line}`);
          }
        }
      }
    }
    
    return finalResult;
  }
  
  private updatePhaseStatus(phase: number, status: WorkflowPhase['status']): void {
    const phaseIndex = this.phases.findIndex(p => p.phase === phase);
    if (phaseIndex !== -1) {
      this.phases[phaseIndex].status = status;
    }
  }
  
  getPhases(): WorkflowPhase[] {
    return [...this.phases];
  }
  
  isRevisionInProgress(): boolean {
    return this.phases[3].status === 'running';
  }
  
  getRevisionCount(): number {
    return this.revisionCount;
  }
  
  private async executeWithRetry<T>(
    operation: () => Promise<T>,
    retries: number = 0
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      // Check if error is retryable
      const isRetryable = this.isRetryableError(error);
      
      if (!isRetryable || retries >= this.maxRetries) {
        throw error;
      }
      
      // Calculate delay with exponential backoff
      const delay = this.retryDelay * Math.pow(2, retries);
      console.log(`Retrying after ${delay}ms (attempt ${retries + 1}/${this.maxRetries})`);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Retry the operation
      return this.executeWithRetry(operation, retries + 1);
    }
  }
  
  private isRetryableError(error: any): boolean {
    // Network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return true;
    }
    
    // Server errors (5xx)
    if (error.message && error.message.includes('500')) {
      return true;
    }
    if (error.message && error.message.includes('502')) {
      return true;
    }
    if (error.message && error.message.includes('503')) {
      return true;
    }
    if (error.message && error.message.includes('504')) {
      return true;
    }
    
    // Rate limiting
    if (error.message && error.message.includes('429')) {
      return true;
    }
    
    // Timeout errors
    if (error.message && (error.message.includes('timeout') || error.message.includes('timed out'))) {
      return true;
    }
    
    return false;
  }
  
}

// Utility function to create and execute workflow
export async function executeMultiPhaseWorkflow(
  sessionId: string,
  message: string,
  fileIds: string[] | undefined,
  callbacks: WorkflowCallbacks
): Promise<WorkflowOrchestrator> {
  const orchestrator = new WorkflowOrchestrator(callbacks);
  await orchestrator.executeWorkflow(sessionId, message, fileIds);
  return orchestrator;
}